import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from "react";

const SCROLL_AMOUNT = 200;
const SCROLL_PADDING = 48;

const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const useScrollableTabList = (activeTabId?: string) => {
    const scrollableRef = useRef<HTMLUListElement>(null);
    const highlightRef = useRef<HTMLDivElement>(null);
    const [isPreviousInactive, setIsPreviousInactive] = useState(true);
    const [isNextInactive, setIsNextInactive] = useState(true);
    const isFirstRender = useRef(true);
    const isManualScrolling = useRef(false);
    const isProgrammaticScroll = useRef(false);
    const manualScrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Suppress auto-centering for a duration after manual scrolling
    const markManualScroll = useCallback(() => {
        isManualScrolling.current = true;
        if (manualScrollTimeoutRef.current) {
            clearTimeout(manualScrollTimeoutRef.current);
        }
        manualScrollTimeoutRef.current = setTimeout(() => {
            isManualScrolling.current = false;
        }, 500);
    }, []);

    // Measure and position the highlight behind the active tab
    const updateHighlightPosition = useCallback(() => {
        const scrollable = scrollableRef.current;
        const highlight = highlightRef.current;
        if (!scrollable || !highlight || !activeTabId) {
            return;
        }

        const activeTab = scrollable.querySelector<HTMLElement>(
            `[data-tab-id="${activeTabId}"]`
        );
        if (!activeTab) {
            return;
        }

        const listRect = scrollable.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const left = tabRect.left - listRect.left + scrollable.scrollLeft;
        const width = tabRect.width;

        highlight.style.left = `${left}px`;
        highlight.style.width = `${width}px`;
    }, [activeTabId]);

    // Scroll the active tab into the center of the visible area
    const scrollActiveTabIntoView = useCallback(() => {
        const scrollable = scrollableRef.current;
        if (!scrollable || !activeTabId) {
            return;
        }

        const activeTab = scrollable.querySelector<HTMLElement>(
            `[data-tab-id="${activeTabId}"]`
        );
        if (!activeTab) {
            return;
        }

        const scrollableWidth = scrollable.offsetWidth;
        const tabLeft = activeTab.offsetLeft;
        const tabWidth = activeTab.offsetWidth;
        const targetScroll = tabLeft - (scrollableWidth - tabWidth) / 2;

        isProgrammaticScroll.current = true;

        scrollable.scrollTo({
            left: targetScroll,
            behavior: prefersReducedMotion() ? "auto" : "smooth"
        });
    }, [activeTabId]);

    // Update nav button visibility based on scroll position
    const updateNavButtonVisibility = useCallback(() => {
        const scrollable = scrollableRef.current;
        if (!scrollable) {
            return;
        }

        const atEnd =
            scrollable.scrollLeft >
            scrollable.scrollWidth -
                scrollable.offsetWidth -
                SCROLL_PADDING * 0.75;
        const atStart = scrollable.scrollLeft <= SCROLL_PADDING;

        setIsNextInactive(atEnd);
        setIsPreviousInactive(atStart);
    }, []);

    // Scroll the tab list by a fixed amount
    const handleScroll = useCallback(
        (direction: 1 | -1) => {
            const scrollable = scrollableRef.current;
            if (!scrollable) {
                return;
            }

            markManualScroll();
            isProgrammaticScroll.current = true;

            scrollable.scrollTo({
                top: 0,
                left: scrollable.scrollLeft + SCROLL_AMOUNT * direction,
                behavior: prefersReducedMotion() ? "auto" : "smooth"
            });
        },
        [markManualScroll]
    );

    // Position highlight on first render without transition
    useLayoutEffect(() => {
        if (isFirstRender.current) {
            const highlight = highlightRef.current;
            if (highlight) {
                highlight.style.transition = "none";
            }

            requestAnimationFrame(() => {
                updateHighlightPosition();
                requestAnimationFrame(() => {
                    if (highlight) {
                        highlight.style.transition = "";
                    }
                    isFirstRender.current = false;
                });
            });
        }
    }, [updateHighlightPosition]);

    // Update highlight and scroll into view on subsequent tab changes
    useEffect(() => {
        if (!isFirstRender.current) {
            updateHighlightPosition();
            scrollActiveTabIntoView();
        }
    }, [updateHighlightPosition, scrollActiveTabIntoView]);

    // Recalculate highlight position and scroll on container resize (debounced)
    useEffect(() => {
        const scrollable = scrollableRef.current;
        if (!scrollable || typeof ResizeObserver !== "function") {
            return;
        }

        let rafId: number | null = null;

        const observer = new ResizeObserver(() => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                updateHighlightPosition();
                if (!isManualScrolling.current) {
                    scrollActiveTabIntoView();
                }
                rafId = null;
            });
        });

        observer.observe(scrollable);

        return () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            if (manualScrollTimeoutRef.current) {
                clearTimeout(manualScrollTimeoutRef.current);
            }
            observer.disconnect();
        };
    }, [updateHighlightPosition, scrollActiveTabIntoView]);

    // Detect manual (user-initiated) horizontal scrolling on the tab list
    useEffect(() => {
        const scrollable = scrollableRef.current;
        if (!scrollable) {
            return;
        }

        const onScroll = () => {
            if (isProgrammaticScroll.current) {
                isProgrammaticScroll.current = false;
                return;
            }
            markManualScroll();
        };

        scrollable.addEventListener("scroll", onScroll, { passive: true });

        return () => {
            scrollable.removeEventListener("scroll", onScroll);
        };
    }, [markManualScroll]);

    // Set up IntersectionObserver to track scroll position for nav buttons
    useEffect(() => {
        const scrollable = scrollableRef.current;
        if (!scrollable || typeof window.IntersectionObserver !== "function") {
            return;
        }

        const options: IntersectionObserverInit = {
            root: scrollable,
            rootMargin: `0px ${SCROLL_PADDING * 0.75}px 0px 0px`,
            threshold: [0.1, 0.9, 1.0]
        };

        const observer = new IntersectionObserver(() => {
            updateNavButtonVisibility();
        }, options);

        const firstItem = scrollable.querySelector("li:first-of-type");
        const lastItem = scrollable.querySelector("li:last-of-type");

        if (firstItem) {
            observer.observe(firstItem);
        }
        if (lastItem) {
            observer.observe(lastItem);
        }

        updateNavButtonVisibility();

        return () => {
            observer.disconnect();
        };
    }, [updateNavButtonVisibility]);

    return {
        scrollableRef,
        highlightRef,
        isPreviousInactive,
        isNextInactive,
        handleScroll
    };
};
